import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { StepResolution } from './stepResolution';
import type {
  TourEnd,
  TourFeedbackSentiment,
  TourTrackingEvent,
  TrackTourEvent,
} from './tourTracking';
import type { ActiveTour, TourId } from './types';

type StepTrackingEvent = Extract<TourTrackingEvent, { stepId: string }>;

// A step re-settles on Back and on new data, so one row per step is what keeps
// these countable as steps.
const useEmitOnce = (trackTourEvent: TrackTourEvent) => {
  const emittedKeys = useRef(new Set<string>());
  return (event: StepTrackingEvent): void => {
    const key = `${event.type}|${event.tourId}|${event.stepId}`;
    if (emittedKeys.current.has(key)) return;
    emittedKeys.current.add(key);
    trackTourEvent(event);
  };
};

const useTourRun = (
  tourId: TourId,
  totalSteps: number,
  startStepNumber: number,
  trackTourEvent: TrackTourEvent
): RefObject<number> => {
  const startedTourId = useRef<TourId | null>(null);
  const startedAt = useRef(performance.now());

  useEffect(() => {
    if (startedTourId.current === tourId) return;
    startedTourId.current = tourId;
    startedAt.current = performance.now();
    trackTourEvent({
      type: 'tour-started',
      tourId,
      totalSteps,
      startStepNumber,
    });
    // Only the tour's identity starts a run; later renders must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourId]);

  return startedAt;
};

type TourTrackingArgs = {
  activeTour: ActiveTour;
  resolution: StepResolution | null;
  trackTourEvent: TrackTourEvent;
  onClose: VoidFunction;
};

type TourTrackingHandlers = {
  completeRun: VoidFunction;
  dismissRun: VoidFunction;
  feedbackSentiment: TourFeedbackSentiment | null;
  giveFeedback: (sentiment: TourFeedbackSentiment) => void;
};

const toDismissalEnd = (stepNumber: number, totalSteps: number): TourEnd =>
  stepNumber === totalSteps ? 'tour-completed' : 'tour-dismissed';

export const useTourTracking = ({
  activeTour,
  resolution,
  trackTourEvent,
  onClose,
}: TourTrackingArgs): TourTrackingHandlers => {
  const { definition, stepIndex } = activeTour;
  const tourId = definition.id;
  const totalSteps = definition.steps.length;
  const stepNumber = stepIndex + 1;
  const step = definition.steps[stepIndex] ?? null;
  const stepId = step?.id;

  const emitOnce = useEmitOnce(trackTourEvent);
  const startedAt = useTourRun(tourId, totalSteps, stepNumber, trackTourEvent);
  const [feedback, setFeedback] = useState<{
    tourId: TourId;
    sentiment: TourFeedbackSentiment;
  } | null>(null);
  const feedbackSentiment =
    feedback?.tourId === tourId ? feedback.sentiment : null;

  useEffect(() => {
    if (!resolution || !stepId || resolution.status === 'resolving') return;
    const stepEvent = { tourId, stepId, stepNumber, totalSteps };
    if (resolution.status === 'fallback') {
      emitOnce({
        type: 'step-anchor-failed',
        ...stepEvent,
        reason: resolution.reason,
      });
    }
    emitOnce({
      type: 'step-viewed',
      ...stepEvent,
      anchorOutcome: resolution.status,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolution]);

  const endRun = useCallback(
    (tourEnd: TourEnd) => {
      trackTourEvent({
        type: tourEnd,
        tourId,
        stepNumber,
        totalSteps,
        durationSeconds: Math.round(
          (performance.now() - startedAt.current) / 1000
        ),
      });
      onClose();
    },
    [onClose, startedAt, stepNumber, totalSteps, tourId, trackTourEvent]
  );

  const giveFeedback = useCallback(
    (sentiment: TourFeedbackSentiment) => {
      setFeedback({ tourId, sentiment });
      trackTourEvent({
        type: 'tour-feedback-given',
        tourId,
        sentiment,
        stepNumber,
        totalSteps,
      });
    },
    [stepNumber, totalSteps, tourId, trackTourEvent]
  );

  return {
    completeRun: () => endRun('tour-completed'),
    dismissRun: () => endRun(toDismissalEnd(stepNumber, totalSteps)),
    feedbackSentiment,
    giveFeedback,
  };
};
